import { Box, SxProps, TextField, Theme, Typography } from '@mui/material';

import { UserType } from '@/models/User';
import { PartnerDashboardDetailsProps } from '@/models/PartnerDashboard';
import { ProgramNames } from '@/models/Program';

import { AdditionalInformationSection } from './AdditionalInformationSection';
import { LocationSelectionSection } from './LocationSelectionSection';

type Props = {
  active: boolean;
  partnerInfo: PartnerDashboardDetailsProps;
  isPartnerNameMissing: boolean;
  userType: UserType | null;
  showErrors: boolean;
  labelCountry: string;
  labelState: string;
  labelCounty: string;
  tabFormSx: SxProps<Theme>;
  sectionSx: SxProps<Theme>;
  sectionTitleSx: SxProps<Theme>;
  onPatchPartner: (patch: Partial<PartnerDashboardDetailsProps>) => void;
  onMarkDirty: () => void;
};

export function PartnerInfoTab({
  active,
  partnerInfo,
  isPartnerNameMissing,
  userType,
  showErrors,
  labelCountry,
  labelState,
  labelCounty,
  tabFormSx,
  sectionSx,
  sectionTitleSx,
  onPatchPartner,
  onMarkDirty,
}: Props) {
  return (
    <Box sx={{ ...(active ? tabFormSx : { display: 'none' }) }}>
      <Box sx={sectionSx}>
        <Typography variant='h6' sx={sectionTitleSx}>
          Partner Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label='Partner Name'
            value={partnerInfo.partnerName ?? ''}
            error={isPartnerNameMissing}
            helperText={isPartnerNameMissing ? 'Partner Name is required' : undefined}
            onChange={(e) => {
              onPatchPartner({ partnerName: e.target.value });
              onMarkDirty();
            }}
            id='partnerName'
            variant='outlined'
            required
          />

          <TextField
            label='City'
            value={partnerInfo.city ?? ''}
            onChange={(e) => {
              onPatchPartner({ city: e.target.value });
              onMarkDirty();
            }}
            id='city'
            variant='outlined'
          />
        </Box>
      </Box>

      <Box sx={sectionSx}>
        <Typography variant='h6' sx={sectionTitleSx}>
          Location
        </Typography>
        <LocationSelectionSection
          disabled={userType !== UserType.Adf}
          showErrors={showErrors}
          partnerInfo={partnerInfo}
          labelCountry={labelCountry}
          labelState={labelState}
          labelCounty={labelCounty}
          patchPartner={(patch) => {
            onPatchPartner(patch);
            onMarkDirty();
          }}
        />
      </Box>

      <Box sx={sectionSx}>
        <Typography variant='h6' sx={sectionTitleSx}>
          Additional Information
        </Typography>
        <AdditionalInformationSection
          notes={partnerInfo.notes}
          specialInstructions={partnerInfo.specialInstructions}
          additionalStickers={partnerInfo.additionalStickers}
          showAdditionalStickers={partnerInfo.programId === ProgramNames.TreeCityUSA}
          disabled={userType !== UserType.Adf}
          onChange={(patch) => {
            onPatchPartner(patch);
            onMarkDirty();
          }}
        />
      </Box>
    </Box>
  );
}
